import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiDatabase, FiAlertCircle, FiCheckCircle, FiCalendar,
  FiUsers, FiTrendingUp, FiDollarSign,
} from 'react-icons/fi';
import * as api from '../../utils/financeBalancesApi';
import FinancialBalancesChatWidget from './FinancialBalancesChatWidget';
import '../../Styles/FinancialBalances.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const PERIODS = Array.from({ length: 12 }, (_, i) => ({
  label: `Period ${String(i + 1).padStart(2, '0')}`,
  value: String(i + 1).padStart(3, '0'),
}));

const fmtINR = (n) => {
  const num = Number(n) || 0;
  if (Math.abs(num) >= 1e7) return `₹${(num / 1e7).toFixed(2)} Cr`;
  if (Math.abs(num) >= 1e5) return `₹${(num / 1e5).toFixed(2)} L`;
  return `₹${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const pct = (n) => `${(Number(n) || 0).toFixed(1)}%`;

const AGING_LABELS = {
  NOT_DUE: 'Not Due', '0_30': '0–30 days',
  '31_60': '31–60 days', '61_90': '61–90 days', ABOVE_90: '90+ days',
};

function Spinner() {
  return <span className="fnb-spinner" />;
}

// ─── Field helpers ────────────────────────────────────────────────────────────

function Field({ label, required, children }) {
  return (
    <div className="fnb-field">
      <label>{label}{required && <span className="fnb-req">*</span>}</label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder }) {
  return (
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  );
}

function PeriodSelect({ value, onChange }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
    </select>
  );
}

// ─── Aging Table ─────────────────────────────────────────────────────────────

function AgingTable({ buckets }) {
  return (
    <div className="fnb-table-wrap">
      <table className="fnb-table">
        <thead>
          <tr><th>Bucket</th><th>Amount</th><th>Share</th><th>Invoices</th></tr>
        </thead>
        <tbody>
          {buckets.map((b, i) => (
            <tr key={b.bucket} style={{ '--row-delay': `${i * 40}ms` }}>
              <td>
                <span className={`fnb-aging fnb-aging-${b.bucket.toLowerCase()}`}>
                  {AGING_LABELS[b.bucket] || b.bucket}
                </span>
              </td>
              <td>{fmtINR(b.amount)}</td>
              <td>
                <div className="fnb-bar-wrap">
                  <div className="fnb-bar-track">
                    <div className="fnb-bar-fill" style={{ width: `${Math.min(b.percentage, 100)}%` }} />
                  </div>
                  <span>{pct(b.percentage)}</span>
                </div>
              </td>
              <td>{b.invoice_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Ledger Results ───────────────────────────────────────────────────────────

function LedgerResults({ data, onPeriodDrill }) {
  const kpis = [
    { icon: <FiDollarSign color="#fff" />, cls: 'ci1', label: 'Opening Balance', value: fmtINR(data.opening_balance) },
    { icon: <FiTrendingUp color="#fff" />, cls: 'ci3', label: 'Closing Balance',  value: fmtINR(data.closing_balance) },
    { icon: <FiTrendingUp color="#fff" />, cls: 'ci4', label: 'Net Movement',     value: fmtINR(data.net_movement) },
    { icon: <FiDatabase  color="#fff" />, cls: 'ci1', label: 'Total Debit',      value: fmtINR(data.total_debit) },
    { icon: <FiDatabase  color="#fff" />, cls: 'ci5', label: 'Total Credit',     value: fmtINR(data.total_credit) },
    { icon: <FiAlertCircle color="#fff" />, cls: 'ci6', label: 'Uncleared Items', value: fmtINR(data.uncleared_items) },
    { icon: <FiTrendingUp color="#fff" />, cls: 'ci3', label: 'YoY Growth',       value: pct(data.yoy_growth_percent) },
  ];

  return (
    <div className="fnb-card">
      <div className="fnb-panel-hd">
        <h3>Ledger Balance — {data.company_code} / FY{data.fiscal_year} / {data.ledger || '0L'}</h3>
        <span className={`fnb-badge ${data.reconciliation_status === 'CLEARED' ? 'fnb-badge-green' : 'fnb-badge-amber'}`}>
          {data.reconciliation_status}
        </span>
      </div>

      <div className="fnb-kpi-grid">
        {kpis.map((k, i) => (
          <div key={k.label} className="fnb-kpi" style={{ '--delay': `${i * 60}ms` }}>
            <div className={`fnb-kpi-icon ${k.cls}`}>{k.icon}</div>
            <div className="fnb-kpi-body">
              <div className="fnb-kpi-label">{k.label}</div>
              <div className="fnb-kpi-value">{k.value}</div>
            </div>
          </div>
        ))}
      </div>

      {data.period_breakdown?.length > 0 && (
        <>
          <div className="fnb-section-header">
            <h4 className="fnb-section-title">Period Breakdown{onPeriodDrill && <span className="fnb-drill-hint"> — click row to drill in</span>}</h4>
          </div>
          <div className="fnb-table-wrap">
            <table className="fnb-table">
              <thead>
                <tr>
                  <th>Period</th><th>Label</th><th>Opening</th>
                  <th>Closing</th><th>Movement</th><th>Debit</th><th>Credit</th>
                </tr>
              </thead>
              <tbody>
                {data.period_breakdown.map((p, i) => (
                  <tr
                    key={p.period}
                    style={{ '--row-delay': `${i * 30}ms`, ...(onPeriodDrill && { cursor: 'pointer' }) }}
                    onClick={onPeriodDrill ? () => onPeriodDrill(p.period) : undefined}
                    title={onPeriodDrill ? `Drill into ${p.period_label || p.period}` : undefined}
                  >
                    <td>{p.period}</td>
                    <td>{p.period_label}</td>
                    <td>{fmtINR(p.opening_balance)}</td>
                    <td>{fmtINR(p.closing_balance)}</td>
                    <td className={p.movement >= 0 ? 'fnb-pos' : 'fnb-neg'}>{fmtINR(p.movement)}</td>
                    <td>{fmtINR(p.debit_amount)}</td>
                    <td>{fmtINR(p.credit_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Vendor Results ───────────────────────────────────────────────────────────

function VendorResults({ data, onVendorDrill }) {
  const kpis = [
    { icon: <FiDollarSign color="#fff" />,   cls: 'ci1', label: 'Total Payables',      value: fmtINR(data.total_payables) },
    { icon: <FiAlertCircle color="#fff" />,  cls: 'ci2', label: 'Overdue Payables',     value: fmtINR(data.overdue_payables) },
    { icon: <FiCheckCircle color="#fff" />,  cls: 'ci3', label: 'Paid in Period',       value: fmtINR(data.paid_in_period) },
    { icon: <FiCalendar color="#fff" />,     cls: 'ci4', label: 'DPO (Days)',           value: data.dpo_days },
    { icon: <FiUsers color="#fff" />,        cls: 'ci5', label: 'Total Vendors',        value: data.total_vendors },
    { icon: <FiAlertCircle color="#fff" />,  cls: 'ci6', label: 'Vendors w/ Overdue',  value: data.vendors_with_overdue },
    { icon: <FiTrendingUp color="#fff" />,   cls: 'ci7', label: 'On-time Payment',      value: pct(data.on_time_payment_percent) },
  ];

  return (
    <div className="fnb-card">
      <div className="fnb-panel-hd">
        <h3>Vendor Balance — {data.company_code} / Period {data.period} / FY{data.fiscal_year}</h3>
      </div>

      <div className="fnb-kpi-grid">
        {kpis.map((k, i) => (
          <div key={k.label} className="fnb-kpi" style={{ '--delay': `${i * 60}ms` }}>
            <div className={`fnb-kpi-icon ${k.cls}`}>{k.icon}</div>
            <div className="fnb-kpi-body">
              <div className="fnb-kpi-label">{k.label}</div>
              <div className="fnb-kpi-value">{k.value}</div>
            </div>
          </div>
        ))}
      </div>

      {data.aging_buckets?.length > 0 && (
        <>
          <div className="fnb-section-header">
            <h4 className="fnb-section-title">Aging Buckets</h4>
          </div>
          <AgingTable buckets={data.aging_buckets} />
        </>
      )}

      {data.top_vendors?.length > 0 && (
        <>
          <div className="fnb-section-header">
            <h4 className="fnb-section-title">Top Vendors{onVendorDrill && <span className="fnb-drill-hint"> — click row to drill in</span>}</h4>
          </div>
          <div className="fnb-table-wrap">
            <table className="fnb-table">
              <thead>
                <tr><th>Code</th><th>Name</th><th>Open Amount</th><th>Overdue</th><th>Oldest (days)</th></tr>
              </thead>
              <tbody>
                {data.top_vendors.map((v, i) => (
                  <tr
                    key={v.vendor_code}
                    style={{ '--row-delay': `${i * 40}ms`, ...(onVendorDrill && { cursor: 'pointer' }) }}
                    onClick={onVendorDrill ? () => onVendorDrill(v.vendor_code) : undefined}
                    title={onVendorDrill ? `Drill into ${v.vendor_name}` : undefined}
                  >
                    <td className="fnb-mono">{v.vendor_code}</td>
                    <td>{v.vendor_name}</td>
                    <td>{fmtINR(v.open_amount)}</td>
                    <td className="fnb-neg">{fmtINR(v.overdue_amount)}</td>
                    <td>{v.oldest_invoice_days}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {data.month_wise_payables?.length > 0 && (
        <>
          <div className="fnb-section-header">
            <h4 className="fnb-section-title">Month-wise Payables</h4>
          </div>
          <div className="fnb-table-wrap">
            <table className="fnb-table">
              <thead>
                <tr><th>Period</th><th>Month</th><th>FY</th><th>Amount</th></tr>
              </thead>
              <tbody>
                {data.month_wise_payables.map((m, i) => (
                  <tr key={`${m.period}-${m.fiscal_year}`} style={{ '--row-delay': `${i * 40}ms` }}>
                    <td>{m.period}</td>
                    <td>{m.month_label}</td>
                    <td>{m.fiscal_year}</td>
                    <td>{fmtINR(m.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Customer Results ─────────────────────────────────────────────────────────

function CustomerResults({ data, onCustomerDrill }) {
  const kpis = [
    { icon: <FiDollarSign color="#fff" />,   cls: 'ci1', label: 'Total Receivables',    value: fmtINR(data.total_receivables) },
    { icon: <FiAlertCircle color="#fff" />,  cls: 'ci2', label: 'Overdue Receivables',   value: fmtINR(data.overdue_receivables) },
    { icon: <FiCheckCircle color="#fff" />,  cls: 'ci3', label: 'Collected in Period',   value: fmtINR(data.collected_in_period) },
    { icon: <FiCalendar color="#fff" />,     cls: 'ci4', label: 'DSO (Days)',            value: data.dso_days },
    { icon: <FiUsers color="#fff" />,        cls: 'ci5', label: 'Total Customers',       value: data.total_customers },
    { icon: <FiAlertCircle color="#fff" />,  cls: 'ci6', label: 'Customers w/ Overdue', value: data.customers_with_overdue },
    { icon: <FiTrendingUp color="#fff" />,   cls: 'ci7', label: 'Collection Efficiency', value: pct(data.collection_efficiency_percent) },
  ];

  return (
    <div className="fnb-card">
      <div className="fnb-panel-hd">
        <h3>Customer Balance — {data.company_code} / Period {data.period} / FY{data.fiscal_year}</h3>
        {data.overdue_alert_count > 0 && (
          <span className="fnb-badge fnb-badge-red">⚠ {data.overdue_alert_count} alerts</span>
        )}
      </div>

      <div className="fnb-kpi-grid">
        {kpis.map((k, i) => (
          <div key={k.label} className="fnb-kpi" style={{ '--delay': `${i * 60}ms` }}>
            <div className={`fnb-kpi-icon ${k.cls}`}>{k.icon}</div>
            <div className="fnb-kpi-body">
              <div className="fnb-kpi-label">{k.label}</div>
              <div className="fnb-kpi-value">{k.value}</div>
            </div>
          </div>
        ))}
      </div>

      {data.aging_buckets?.length > 0 && (
        <>
          <div className="fnb-section-header">
            <h4 className="fnb-section-title">Aging Buckets</h4>
          </div>
          <AgingTable buckets={data.aging_buckets} />
        </>
      )}

      {data.top_customers?.length > 0 && (
        <>
          <div className="fnb-section-header">
            <h4 className="fnb-section-title">Top Customers{onCustomerDrill && <span className="fnb-drill-hint"> — click row to drill in</span>}</h4>
          </div>
          <div className="fnb-table-wrap">
            <table className="fnb-table">
              <thead>
                <tr><th>Code</th><th>Name</th><th>Open Amount</th><th>Overdue</th><th>Oldest (days)</th></tr>
              </thead>
              <tbody>
                {data.top_customers.map((c, i) => (
                  <tr
                    key={c.customer_code}
                    style={{ '--row-delay': `${i * 40}ms`, ...(onCustomerDrill && { cursor: 'pointer' }) }}
                    onClick={onCustomerDrill ? () => onCustomerDrill(c.customer_code) : undefined}
                    title={onCustomerDrill ? `Drill into ${c.customer_name}` : undefined}
                  >
                    <td className="fnb-mono">{c.customer_code}</td>
                    <td>{c.customer_name}</td>
                    <td>{fmtINR(c.open_amount)}</td>
                    <td className="fnb-neg">{fmtINR(c.overdue_amount)}</td>
                    <td>{c.oldest_invoice_days}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'ledger',   label: 'Ledger Balance' },
  { id: 'vendor',   label: 'Vendor Balance' },
  { id: 'customer', label: 'Customer Balance' },
];

export default function FinancialBalancesApp() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ledger');

  const [ledger, setLedger] = useState({
    company_code: '1100', fiscal_year: '2025',
    period_from: '001', period_to: '012',
    gl_account: '', ledger: '', currency: '',
  });
  const [ledgerResult, setLedgerResult] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState('');

  const [vendor, setVendor] = useState({
    company_code: '1100', fiscal_year: '2025', period: '003',
    vendor_code: '', vendor_group: '', aging_buckets: '', currency: '',
  });
  const [vendorResult, setVendorResult] = useState(null);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendorError, setVendorError] = useState('');

  const [customer, setCustomer] = useState({
    company_code: '1100', fiscal_year: '2025', period: '003',
    customer_code: '', customer_group: '', aging_buckets: '', currency: '',
  });
  const [customerResult, setCustomerResult] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerError, setCustomerError] = useState('');

  const stripEmpty = (obj) => {
    const out = { ...obj };
    Object.keys(out).forEach((k) => { if (!out[k]) delete out[k]; });
    return out;
  };

  const runLedger = async () => {
    setLedgerError(''); setLedgerLoading(true);
    try { setLedgerResult(await api.fetchLedgerBalance(stripEmpty(ledger))); }
    catch (e) { setLedgerError(e.message); }
    finally { setLedgerLoading(false); }
  };

  const runVendor = async () => {
    setVendorError(''); setVendorLoading(true);
    try { setVendorResult(await api.fetchVendorBalance(stripEmpty(vendor))); }
    catch (e) { setVendorError(e.message); }
    finally { setVendorLoading(false); }
  };

  const runCustomer = async () => {
    setCustomerError(''); setCustomerLoading(true);
    try { setCustomerResult(await api.fetchCustomerBalance(stripEmpty(customer))); }
    catch (e) { setCustomerError(e.message); }
    finally { setCustomerLoading(false); }
  };

  const drillLedgerPeriod = async (period) => {
    setLedgerError(''); setLedgerLoading(true);
    try { setLedgerResult(await api.fetchLedgerBalance(stripEmpty({ ...ledger, period_from: period, period_to: period }))); }
    catch (e) { setLedgerError(e.message); }
    finally { setLedgerLoading(false); }
  };

  const drillVendor = async (vendorCode) => {
    setVendorError(''); setVendorLoading(true);
    try { setVendorResult(await api.fetchVendorBalance(stripEmpty({ ...vendor, vendor_code: vendorCode }))); }
    catch (e) { setVendorError(e.message); }
    finally { setVendorLoading(false); }
  };

  const drillCustomer = async (customerCode) => {
    setCustomerError(''); setCustomerLoading(true);
    try { setCustomerResult(await api.fetchCustomerBalance(stripEmpty({ ...customer, customer_code: customerCode }))); }
    catch (e) { setCustomerError(e.message); }
    finally { setCustomerLoading(false); }
  };

  return (
    <div className="fnb-app">
      {/* Ambient orbs */}
      <div className="fnb-bg-orbs" aria-hidden="true">
        <span className="fnb-bg-orb fnb-bg-orb-1" />
        <span className="fnb-bg-orb fnb-bg-orb-2" />
        <span className="fnb-bg-orb fnb-bg-orb-3" />
      </div>

      {/* Topbar */}
      <div className="fnb-topbar">
        <button className="fnb-topbar-back" onClick={() => navigate('/home')}>
          <FiArrowLeft size={14} /> Back to Home
        </button>
        <span className="fnb-topbar-title">Finance Balances</span>
        <span className="fnb-topbar-sub">Ledger · Vendor · Customer</span>
        <div className="fnb-ai-badge"><span className="fnb-ai-dot" />AI Powered</div>
      </div>

      {/* Tabs */}
      <div className="fnb-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`fnb-tab-btn${activeTab === t.id ? ' active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="fnb-content">
        <div key={activeTab} className="fnb-tab-panel">

          {/* ── Ledger Tab ── */}
          {activeTab === 'ledger' && (
            <>
              <div className="fnb-card">
                <div className="fnb-panel-hd">
                  <h3>Ledger Balance Parameters</h3>
                  <small>Opening Balance · Closing Balance · Net Movement</small>
                </div>
                <div className="fnb-form-grid">
                  <Field label="Company Code" required>
                    <TextInput value={ledger.company_code} onChange={(v) => setLedger((p) => ({ ...p, company_code: v }))} placeholder="1100" />
                  </Field>
                  <Field label="Fiscal Year" required>
                    <TextInput value={ledger.fiscal_year} onChange={(v) => setLedger((p) => ({ ...p, fiscal_year: v }))} placeholder="2025" />
                  </Field>
                  <Field label="Period From" required>
                    <PeriodSelect value={ledger.period_from} onChange={(v) => setLedger((p) => ({ ...p, period_from: v }))} />
                  </Field>
                  <Field label="Period To" required>
                    <PeriodSelect value={ledger.period_to} onChange={(v) => setLedger((p) => ({ ...p, period_to: v }))} />
                  </Field>
                  <Field label="GL Account">
                    <TextInput value={ledger.gl_account} onChange={(v) => setLedger((p) => ({ ...p, gl_account: v }))} placeholder="e.g. 0000201101" />
                  </Field>
                  <Field label="Ledger Type">
                    <TextInput value={ledger.ledger} onChange={(v) => setLedger((p) => ({ ...p, ledger: v }))} placeholder="e.g. 0L" />
                  </Field>
                  <Field label="Currency">
                    <TextInput value={ledger.currency} onChange={(v) => setLedger((p) => ({ ...p, currency: v }))} placeholder="e.g. USD" />
                  </Field>
                </div>
                <div className="fnb-form-footer">
                  <span className="fnb-req-note">* Required fields</span>
                  <button
                    className="fnb-btn fnb-btn-primary"
                    onClick={runLedger}
                    disabled={ledgerLoading || !ledger.company_code || !ledger.fiscal_year}
                  >
                    {ledgerLoading ? <><Spinner /> Running…</> : 'Run Analysis →'}
                  </button>
                </div>
                {ledgerError && <div className="fnb-alert-err"><FiAlertCircle size={14} />{ledgerError}</div>}
              </div>
              {ledgerResult && <LedgerResults data={ledgerResult} onPeriodDrill={drillLedgerPeriod} />}
            </>
          )}

          {/* ── Vendor Tab ── */}
          {activeTab === 'vendor' && (
            <>
              <div className="fnb-card">
                <div className="fnb-panel-hd">
                  <h3>Vendor Balance Parameters</h3>
                  <small>Payables · Overdue · Aging</small>
                </div>
                <div className="fnb-form-grid">
                  <Field label="Company Code" required>
                    <TextInput value={vendor.company_code} onChange={(v) => setVendor((p) => ({ ...p, company_code: v }))} placeholder="1100" />
                  </Field>
                  <Field label="Fiscal Year" required>
                    <TextInput value={vendor.fiscal_year} onChange={(v) => setVendor((p) => ({ ...p, fiscal_year: v }))} placeholder="2025" />
                  </Field>
                  <Field label="Period" required>
                    <PeriodSelect value={vendor.period} onChange={(v) => setVendor((p) => ({ ...p, period: v }))} />
                  </Field>
                  <Field label="Vendor Code">
                    <TextInput value={vendor.vendor_code} onChange={(v) => setVendor((p) => ({ ...p, vendor_code: v }))} placeholder="e.g. 0001000123" />
                  </Field>
                  <Field label="Vendor Group">
                    <TextInput value={vendor.vendor_group} onChange={(v) => setVendor((p) => ({ ...p, vendor_group: v }))} placeholder="e.g. DOM" />
                  </Field>
                  <Field label="Aging Buckets">
                    <TextInput value={vendor.aging_buckets} onChange={(v) => setVendor((p) => ({ ...p, aging_buckets: v }))} placeholder="e.g. 30,60,90" />
                  </Field>
                  <Field label="Currency">
                    <TextInput value={vendor.currency} onChange={(v) => setVendor((p) => ({ ...p, currency: v }))} placeholder="e.g. USD" />
                  </Field>
                </div>
                <div className="fnb-form-footer">
                  <span className="fnb-req-note">* Required fields</span>
                  <button
                    className="fnb-btn fnb-btn-primary"
                    onClick={runVendor}
                    disabled={vendorLoading || !vendor.company_code || !vendor.fiscal_year}
                  >
                    {vendorLoading ? <><Spinner /> Running…</> : 'Run Analysis →'}
                  </button>
                </div>
                {vendorError && <div className="fnb-alert-err"><FiAlertCircle size={14} />{vendorError}</div>}
              </div>
              {vendorResult && <VendorResults data={vendorResult} onVendorDrill={drillVendor} />}
            </>
          )}

          {/* ── Customer Tab ── */}
          {activeTab === 'customer' && (
            <>
              <div className="fnb-card">
                <div className="fnb-panel-hd">
                  <h3>Customer Balance Parameters</h3>
                  <small>Receivables · Overdue · Aging</small>
                </div>
                <div className="fnb-form-grid">
                  <Field label="Company Code" required>
                    <TextInput value={customer.company_code} onChange={(v) => setCustomer((p) => ({ ...p, company_code: v }))} placeholder="1100" />
                  </Field>
                  <Field label="Fiscal Year" required>
                    <TextInput value={customer.fiscal_year} onChange={(v) => setCustomer((p) => ({ ...p, fiscal_year: v }))} placeholder="2025" />
                  </Field>
                  <Field label="Period" required>
                    <PeriodSelect value={customer.period} onChange={(v) => setCustomer((p) => ({ ...p, period: v }))} />
                  </Field>
                  <Field label="Customer Code">
                    <TextInput value={customer.customer_code} onChange={(v) => setCustomer((p) => ({ ...p, customer_code: v }))} placeholder="e.g. 0001000048" />
                  </Field>
                  <Field label="Customer Group">
                    <TextInput value={customer.customer_group} onChange={(v) => setCustomer((p) => ({ ...p, customer_group: v }))} placeholder="e.g. Z001" />
                  </Field>
                  <Field label="Aging Buckets">
                    <TextInput value={customer.aging_buckets} onChange={(v) => setCustomer((p) => ({ ...p, aging_buckets: v }))} placeholder="e.g. 30,60,90" />
                  </Field>
                  <Field label="Currency">
                    <TextInput value={customer.currency} onChange={(v) => setCustomer((p) => ({ ...p, currency: v }))} placeholder="e.g. USD" />
                  </Field>
                </div>
                <div className="fnb-form-footer">
                  <span className="fnb-req-note">* Required fields</span>
                  <button
                    className="fnb-btn fnb-btn-primary"
                    onClick={runCustomer}
                    disabled={customerLoading || !customer.company_code || !customer.fiscal_year}
                  >
                    {customerLoading ? <><Spinner /> Running…</> : 'Run Analysis →'}
                  </button>
                </div>
                {customerError && <div className="fnb-alert-err"><FiAlertCircle size={14} />{customerError}</div>}
              </div>
              {customerResult && <CustomerResults data={customerResult} onCustomerDrill={drillCustomer} />}
            </>
          )}

        </div>
      </div>

      {/* Finance AI Chat Widget */}
      <FinancialBalancesChatWidget />
    </div>
  );
}
